import React, { useEffect, useState, useRef } from 'react'
import {
  Box,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogContent,
} from '@mui/material'
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto'
import DeleteIcon from '@mui/icons-material/Delete'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import imageCompression from 'browser-image-compression'
import {
  getTreePhotos,
  deleteTreePhoto,
  uploadTreePhotos,
  getPropertiesByID,
} from '../../services'
import { getTreePhotoUrl } from '../../utils/imageUrl'

import 'swiper/css'
import 'swiper/css/pagination'

const MAX_SIZE_KB = 200
const compressionOptions = {
  maxSizeMB: MAX_SIZE_KB / 1024,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  fileType: 'image/webp',
  initialQuality: 0.75,
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const isUuid = value => UUID_RE.test(String(value || ''))

const getPhotoDisplayUrl = (photo, treeId, variant = 'md') =>
  photo?.variants?.[variant] || photo?.url || getTreePhotoUrl(treeId, photo?.filename)

/**
 * TreePhotoGallery - displays and optionally manages photos for a tree.
 *
 * Props:
 *   treeId   – tree ID (required)
 *   treeCode – tree code (fallback to resolve treeId when missing)
 *   editable – show add/delete controls (default false)
 *   token    – auth token string (required when editable=true)
 *   compact  – show only the primary photo as a small thumbnail (default false)
 */
const TreePhotoGallery = ({
  treeId,
  treeCode,
  editable = false,
  token,
  compact = false,
  onImageClick,
}) => {
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [lightbox, setLightbox] = useState(null) // url string
  const [activeIndex, setActiveIndex] = useState(0)
  const [resolvedTreeId, setResolvedTreeId] = useState(null)
  const fileInputRef = useRef(null)
  const swiperRef = useRef(null)

  const effectiveTreeId = resolvedTreeId || (isUuid(treeId) ? String(treeId) : null)

  const loadPhotos = id => {
    if (!id) {
      setPhotos([])
      setLoading(false)
      return
    }
    setLoading(true)
    getTreePhotos(id)
      .then(data => setPhotos(Array.isArray(data) ? data : []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadPhotos(effectiveTreeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTreeId])

  useEffect(() => {
    setActiveIndex(0)
    if (swiperRef.current) {
      const swiper = swiperRef.current
      if (swiper.params?.loop) swiper.slideToLoop(0, 0)
      else swiper.slideTo(0, 0)
      requestAnimationFrame(() => swiper.update())
    }
  }, [effectiveTreeId, photos.length])

  useEffect(() => {
    let active = true
    const candidate = treeId || treeCode
    if (!candidate) {
      setResolvedTreeId(null)
      return () => {
        active = false
      }
    }
    if (isUuid(candidate)) {
      setResolvedTreeId(String(candidate))
      return () => {
        active = false
      }
    }
    getPropertiesByID(candidate)
      .then(properties => {
        if (!active) return
        const resolved = properties?.id
        setResolvedTreeId(isUuid(resolved) ? String(resolved) : null)
      })
      .catch(() => {
        if (active) setResolvedTreeId(null)
      })
    return () => {
      active = false
    }
  }, [treeId, treeCode])

  const handleDelete = async photoId => {
    try {
      await deleteTreePhoto(photoId, token)
      setPhotos(prev => prev.filter(p => p.id_tp !== photoId))
    } catch (err) {
      console.error('Error eliminando foto:', err)
    }
  }

  const handleFileSelect = async e => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    if (!effectiveTreeId) return
    setUploading(true)
    const processed = []
    for (const file of files) {
      try {
        let processedFile = file
        if (file.size > MAX_SIZE_KB * 1024) {
          processedFile = await imageCompression(file, compressionOptions)
        }
        processed.push({ file: processedFile, originalName: file.name })
      } catch (err) {
        console.error('Error procesando imagen:', err)
      }
    }
    if (processed.length > 0) {
      try {
        await uploadTreePhotos(effectiveTreeId, processed, token)
        loadPhotos(effectiveTreeId)
      } catch (err) {
        console.error('Error subiendo fotos:', err)
      }
    }
    setUploading(false)
    e.target.value = ''
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  // Compact mode: only primary photo (order=0)
  if (compact) {
    const primary = photos.find(p => p.order === 0) || photos[0]
    if (!primary) return null
    const url = getPhotoDisplayUrl(primary, effectiveTreeId, 'th')
    const handleClick = onImageClick ?? (() => setLightbox(url))
    return (
      <Box
        onClick={handleClick}
        sx={{
          width: '100%',
          aspectRatio: '16/9',
          overflow: 'hidden',
          borderRadius: 1.5,
          cursor: 'zoom-in',
          bgcolor: 'action.hover',
          position: 'relative',
        }}>
        <Box
          component="img"
          src={url}
          alt="Foto principal"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <ZoomInIcon
          sx={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            fontSize: 18,
            color: 'white',
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
          }}
        />
        {!onImageClick && lightbox && (
          <LightboxDialog url={lightbox} onClose={() => setLightbox(null)} />
        )}
      </Box>
    )
  }

  // Non-editable: carousel view
  if (!editable) {
    if (photos.length === 0) return null

    const hasMany = photos.length > 1
    const current = photos[activeIndex] || photos[0]
    const currentLabel =
      current?.originalName || current?.filename || `Foto ${activeIndex + 1}`
    const isPrimary = current?.order === 0

    return (
      <Box>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%',
            borderRadius: 1.5,
            overflow: 'hidden',
            bgcolor: 'action.hover',
            '& .swiper, & .swiper-wrapper, & .swiper-slide': {
              height: '100%',
            },
            '& .swiper-pagination-bullets': {
              bottom: 8,
            },
            '& .swiper-pagination-bullet': {
              width: 6,
              height: 6,
              opacity: 1,
              bgcolor: 'rgba(255,255,255,0.65)',
            },
            '& .swiper-pagination-bullet-active': {
              bgcolor: 'primary.main',
            },
          }}>
          <Box sx={{ position: 'absolute', inset: 0 }}>
            <Swiper
              modules={[Pagination]}
              pagination={{ clickable: true }}
              loop={hasMany}
              observer={true}
              observeParents={true}
              observeSlideChildren={true}
              onSwiper={swiper => {
                swiperRef.current = swiper
              }}
              onSlideChange={swiper => setActiveIndex(swiper.realIndex)}
              style={{ width: '100%', height: '100%' }}>
              {photos.map(photo => {
                const url = getPhotoDisplayUrl(photo, effectiveTreeId, 'md')
                return (
                  <SwiperSlide key={photo.id_tp}>
                    <Box
                      onClick={() => setLightbox(url)}
                      sx={{
                        width: '100%',
                        height: '100%',
                        cursor: 'zoom-in',
                      }}>
                      <Box
                        component="img"
                        src={url}
                        alt={photo.originalName || 'Foto del árbol'}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </Box>

          {isPrimary && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'warning.main',
                color: 'white',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: 0.4,
              }}>
              PRINCIPAL
            </Box>
          )}

          {hasMany && (
            <>
              <IconButton
                aria-label="Anterior"
                onClick={() => swiperRef.current?.slidePrev()}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'rgba(0,0,0,0.35)',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                }}>
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                aria-label="Siguiente"
                onClick={() => swiperRef.current?.slideNext()}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'rgba(0,0,0,0.35)',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                }}>
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              px: 1.25,
              py: 0.75,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background:
                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)',
            }}>
            <Typography
              variant="caption"
              sx={{ color: 'common.white', pr: 1, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
              {currentLabel}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'common.white', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
              {activeIndex + 1}/{photos.length}
            </Typography>
          </Box>
        </Box>

        {lightbox && <LightboxDialog url={lightbox} onClose={() => setLightbox(null)} />}
      </Box>
    )
  }

  // Full gallery mode
  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <Grid container spacing={1}>
        {photos.map(photo => {
          const url = getPhotoDisplayUrl(photo, effectiveTreeId, 'th')
          const isPrimary = photo.order === 0
          return (
            <Grid item xs={6} key={photo.id_tp}>
              <Box
                sx={{
                  position: 'relative',
                  paddingTop: '100%',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: isPrimary ? 'warning.main' : 'divider',
                  cursor: 'zoom-in',
                }}
                onClick={() => setLightbox(url)}>
                <Box
                  component="img"
                  src={url}
                  alt={photo.originalName || `Foto ${photo.order + 1}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />

                {/* Primary star badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    bgcolor: isPrimary ? 'warning.main' : 'rgba(0,0,0,0.35)',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  {isPrimary ? (
                    <StarIcon sx={{ fontSize: 13, color: 'white' }} />
                  ) : (
                    <StarBorderIcon sx={{ fontSize: 13, color: 'white' }} />
                  )}
                </Box>

                {/* Delete button (editable only) */}
                {editable && (
                  <Tooltip title="Eliminar foto" arrow>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation()
                        handleDelete(photo.id_tp)
                      }}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                        width: 24,
                        height: 24,
                      }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Bottom label */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: isPrimary ? 'warning.main' : 'rgba(0,0,0,0.55)',
                    color: 'white',
                    textAlign: 'center',
                    py: 0.25,
                  }}>
                  <Typography sx={{ fontSize: '0.58rem', lineHeight: 1.2 }}>
                    {isPrimary ? 'PRINCIPAL' : `${(photo.size / 1024).toFixed(0)}KB`}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )
        })}

        {/* Add photo slot (editable only) */}
        {editable && (
          <Grid item xs={6}>
            <Box
              onClick={() => !uploading && fileInputRef.current?.click()}
              sx={{
                paddingTop: '100%',
                position: 'relative',
                borderRadius: 1.5,
                border: '2px dashed',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                cursor: uploading ? 'default' : 'pointer',
                transition: 'all 0.15s',
                '&:hover': !uploading
                  ? { borderColor: 'primary.main', bgcolor: 'primary.50' }
                  : {},
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                }}>
                {uploading ? (
                  <CircularProgress size={22} />
                ) : (
                  <>
                    <AddAPhotoIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Agregar
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Lightbox */}
      {lightbox && <LightboxDialog url={lightbox} onClose={() => setLightbox(null)} />}
    </Box>
  )
}

function LightboxDialog({ url, onClose }) {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent
        sx={{ p: 1, bgcolor: '#000', display: 'flex', justifyContent: 'center' }}
        onClick={onClose}>
        <Box
          component="img"
          src={url}
          alt="Foto árbol"
          sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </DialogContent>
    </Dialog>
  )
}

export default TreePhotoGallery
